import React, { useContext } from 'react';
import { useRef } from 'react';
import { Animated, Platform } from 'react-native';
import { createGlobalState } from 'react-native-global-state-hooks';
import { useIsDisabledByParent } from '../../IsDisabledProvider';
import { SideDrawerContainerContext } from '../../SideDrawer/SideDrawerContainer';
import { useIsFocused } from '@react-navigation/native';

export type UseHoverStateArgs = {
    rowKey: string;
};

export type UseHoverStateReturnValue = {
    isHovered: boolean;
    isHoverAnimating: boolean;
    onRowHover: (isHovered: boolean) => void;
    animation: Animated.Value;
};

const ANIMATION_DURATION = 300;
const SET_AS_HOVERED_DELAY = 350;

/**
 * Caution needs to be taken when setting a row to be hovered.  It's common for a user to move their mouse
 * across many rows while scanning.  We only want to hover when the user finalizes their mouse over a row.
 *
 * To do this we utilize a timeout to set a row to hovered.  We want to ensure that only the last row hovered
 * is set to hovered.  This "mutex" stores the last row hovered.
 */
let pendingHoverMutex: string | null = null;

const useGlobalRowHoveredKeyState = createGlobalState<string | null>(null);

export const useHoverState = ({ rowKey }: UseHoverStateArgs): UseHoverStateReturnValue => {
    const { isSideDrawerOpen } = useContext(SideDrawerContainerContext);
    const isDisabledByParent = useIsDisabledByParent();
    const [hoveredRowKey, setGlobalHoveredRowKey] = useGlobalRowHoveredKeyState();
    const animation = useRef(new Animated.Value(0)).current;
    const [isHoverAnimating, setIsHoverAnimating] = React.useState(false);
    const isHovered = hoveredRowKey === rowKey && !isDisabledByParent && !isSideDrawerOpen;
    const handleOnRowHover = (isHovered: boolean) => {
        if (isHovered) {
            // Set this row as the pending row to be hovered
            pendingHoverMutex = rowKey;
            setTimeout(() => {
                // Ensure this is the last row hovered
                if (pendingHoverMutex === rowKey) {
                    setGlobalHoveredRowKey(rowKey);
                }
            }, SET_AS_HOVERED_DELAY);
        } else {
            if (hoveredRowKey === rowKey) {
                setGlobalHoveredRowKey(null);
            } else if (pendingHoverMutex === rowKey) {
                // Cancel the pending hover
                pendingHoverMutex = null;
            }
        }
    };

    const isFocused = useIsFocused();
    React.useEffect(() => {
        if (!isFocused) {
            handleOnRowHover(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]);

    React.useEffect(() => {
        if (isHovered) {
            setIsHoverAnimating(true);
        }

        Animated.timing(animation, {
            toValue: isHovered ? 1 : 0,
            duration: ANIMATION_DURATION,
            useNativeDriver: Platform.OS === 'web',
        }).start(({ finished }) => {
            if (finished && !isHovered) {
                setIsHoverAnimating(false);
            }
        });
    }, [isHovered, animation]);

    return {
        isHovered,
        onRowHover: handleOnRowHover,
        animation,
        isHoverAnimating,
    };
};
